import { useNavigate, useParams } from 'react-router-dom';
import { PermissionGuard } from '@/features/auth';
import { useFeeQuery } from '../hooks/useFeeQuery';
import { useFeeMutations } from '../hooks/useFeeMutations';
import { FeeForm } from '../components/FeeForm';
import type { FeeFormOutput } from '../schemas/fee.schema';
import { Alert, TextLink } from '@/shared/components';

interface FeeDetailProps {
    isNew?: boolean;
}

export function FeeDetail({ isNew = false }: FeeDetailProps) {
    const { id } = useParams();
    const navigate = useNavigate();
    const query = useFeeQuery(isNew ? undefined : id);
    const { createMutation, updateMutation } = useFeeMutations();

    if (!isNew) {
        if (!id) return <p className="text-mp-error">Invalid fee ID.</p>;
        if (query.isLoading) return <p>Loading fee...</p>;
        if (query.isError || !query.data) {
            return <Alert variant="error"><span>Fee not found or failed to load.</span></Alert>;
        }
    }

    const fee = query.data;

    const handleSubmit = (data: FeeFormOutput) => {
        if (isNew) {
            createMutation.mutate(data, {
                onSuccess: (created) => navigate(`/smr-admin/fees/${created.id}`),
            });
        } else if (id) {
            updateMutation.mutate({ id, payload: data });
        }
    };

    return (
        <section className="space-y-4">
            <div className="flex items-center gap-2">
                <TextLink to="/smr-admin/fees" color="secondary" className="text-sm">&larr; Back to Fees</TextLink>
            </div>
            <h1 className="text-2xl font-semibold">{isNew ? 'New Fee' : (fee?.name ?? `Fee ${id}`)}</h1>

            <PermissionGuard permission={{ resource: 'smr', action: isNew ? 'fee.add' : 'fee.edit' }}>
                <FeeForm
                    initialValues={fee}
                    onSubmit={handleSubmit}
                    isLoading={createMutation.isPending || updateMutation.isPending}
                    submitLabel={isNew ? 'Create Fee' : 'Save Changes'}
                />
            </PermissionGuard>
        </section>
    );
}
